import { useNavigate, useSearchParams } from 'react-router';
import { ConnectionsTable } from '@viq-admin/screens';
import { useUrlTableState, type TableState } from '@viq-admin/ui';
import { integrationsApi } from '../../api/integrationsApi';

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — an inline literal here would bust
 * DataTable's `effective` memo (and, through it, `prepared`/`finalColumns`)
 * on every render for no reason. See DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'companyName', order: 'ascend' },
};

/**
 * Cross-tenant list of CRM connections (one per company per source). Thin
 * wrapper around the shared `ConnectionsTable`
 * (`packages/screens/src/integrations/ConnectionsTable.tsx`, p6 Task 2) —
 * Refine's `useTable`/`useDelete` are gone; `ConnectionsTable` owns its own
 * data fetching (react-query over `integrationsApi.listConnections`) and its
 * own delete flow (confirm dialog + toast), both parameterized by the
 * injected `api`.
 *
 * `read Integration` gates the whole page via the route's `RequireCan`
 * (App.tsx). `manage Integration` gates both the row Delete action and the
 * Create button inside `ConnectionsTable` via `manageCap` — a
 * `super_admin_readonly` sees neither.
 *
 * Row-level Test Connection lives inside `ConnectionsTable`'s kebab (Open /
 * Test Connection / Delete), gated by the same `manageCap` as Delete —
 * console parity with the pre-lift list. The connection detail header
 * carries its own Test too (`IntegrationDetail.tsx` → `ConnectionDetail`).
 *
 * Table state stays URL-synced exactly as the pre-lift list had it
 * (`useUrlTableState` over `useSearchParams`, seeded by `DEFAULT_STATE`) —
 * the router lives HERE, threaded through `ConnectionsTable`'s state seam,
 * so an operator's sort/search survives opening a row and coming back, and a
 * bookmarked sorted-list URL still reproduces the view.
 */
export function IntegrationList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);

  return (
    <ConnectionsTable
      api={integrationsApi}
      onOpen={(id) => navigate(`/integrations/${id}`)}
      onCreate={() => navigate('/integrations/new')}
      manageCap="manage:Integration"
      state={state}
      onStateChange={setState}
      defaultState={DEFAULT_STATE}
    />
  );
}
